import { useState, useEffect, useRef } from "react";
import { useDocPrice, priceLabel } from "@/utils/docPrices";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  ShieldAlert, FileText, Download, CheckCircle, Clock, Scale, Mail,
  ChevronDown, ChevronUp, Ban, Copyright, MessageSquareWarning,
  PhoneOff, FileWarning, Lock, PenTool,
} from "lucide-react";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const PRICE = "9.99";

const USE_CASES = [
  { icon: Ban,                   title: "Harassment & Stalking",       desc: "Formally demand that someone stop contacting, following, or intimidating you, and create a paper trail if it escalates.", tone: "gray" },
  { icon: MessageSquareWarning,  title: "Defamation & Slander",        desc: "Demand a retraction of false statements damaging your reputation, and removal of defamatory posts or reviews.", tone: "mint" },
  { icon: Copyright,             title: "Copyright & Trademark",       desc: "Stop unauthorized use of your photos, writing, logo, or brand name by another person or business.", tone: "black" },
  { icon: PhoneOff,              title: "Debt Collector Calls",        desc: "Invoke your FDCPA right to require a collection agency to stop contacting you in writing.", tone: "black" },
  { icon: FileWarning,           title: "Breach of Contract",          desc: "Put a party on formal notice that they are violating your agreement and must cure the breach.", tone: "gray" },
  { icon: Scale,                 title: "Other Unlawful Conduct",      desc: "Describe any other conduct in your own words and generate a properly structured demand letter.", tone: "mint" },
];

const STEPS = (P) => [
  { n: "1", title: "Choose your situation", desc: "Pick the type of violation: harassment, defamation, infringement, debt collection, breach of contract, or other." },
  { n: "2", title: "Fill in the details",   desc: "Enter your information, the recipient's information, and a factual description of the conduct you want stopped." },
  { n: "3", title: "Set your deadline",     desc: "Choose how many days the recipient has to comply and how the letter will be delivered." },
  { n: "4", title: "Download instantly",    desc: `Preview the letter live, ${P === "free" ? "" : `pay ${P}, `}and download a print-ready PDF you can sign and send.` },
];

const FEATURES = [
  { icon: FileText, title: "Attorney-style formatting", desc: "Proper letterhead, subject line, demand paragraphs, and reservation-of-rights language." },
  { icon: Clock,    title: "Ready in minutes",          desc: "No appointments or consultation fees. Fill out a form and download immediately." },
  { icon: PenTool,  title: "Signature included",        desc: "Add a handwriting-style signature or upload an image of your own." },
  { icon: Mail,     title: "Delivery language built in",desc: "Certified mail, email, or hand delivery notations are added automatically." },
  { icon: Download, title: "Instant PDF download",      desc: "Print-ready letter-size PDF. Re-download any time from your account." },
  { icon: Lock,     title: "Private & secure",          desc: "Your details are used to build your document and are never sold or shared." },
];

const FAQS = [
  {
    q: "What is a cease and desist letter?",
    a: "A cease and desist letter is a formal written demand asking someone to stop a specific activity that harms you or violates your legal rights. It states what the conduct is, demands that it stop by a certain date, and warns of legal consequences if it continues. It is commonly used for harassment, defamation, copyright and trademark infringement, debt collection, and contract breaches.",
  },
  {
    q: "Is a cease and desist letter legally binding?",
    a: "A cease and desist letter is not a court order, so it cannot by itself force someone to act. What it does is put the recipient on formal notice, document your objection with a date, and demonstrate that you attempted to resolve the matter before litigating. Many recipients comply once they receive one, and courts often look favorably on a party that gave notice first.",
  },
  {
    q: "Do I need a lawyer to send a cease and desist letter?",
    a: "No. Individuals and businesses can and regularly do send cease and desist letters on their own behalf. A letter on an attorney's letterhead may carry more weight, but a clear, factual, properly structured letter you send yourself still creates legal notice and a documented record. For high-value or complex disputes, consult a licensed attorney in your state.",
  },
  {
    q: "How do I send a cease and desist letter?",
    a: "Certified mail with return receipt requested is the standard method, because it produces proof that the recipient received it. Many people send by certified mail and email at the same time. Always keep a copy of the signed letter along with the delivery receipt or tracking record.",
  },
  {
    q: "Can I use a cease and desist letter to stop debt collectors?",
    a: "Yes. Under the Fair Debt Collection Practices Act (15 U.S.C. § 1692c(c)), a debt collector must stop contacting you once it receives a written request to cease communication. After that, it may only contact you to confirm it is stopping or to tell you it intends to pursue a specific legal remedy. Choose the debt collection option and the letter cites that statute for you.",
  },
  {
    q: "What happens if the person ignores my letter?",
    a: "If the conduct continues past your deadline, your letter becomes valuable evidence. Depending on the situation, next steps can include filing a civil lawsuit for injunctive relief and damages, submitting a DMCA takedown notice, filing a police report for harassment, or filing a complaint with a regulator such as the CFPB for debt collection violations.",
  },
  {
    q: "How much does it cost?",
    a: `A cease and desist letter from MintSlip costs $${PRICE} as a one-time purchase, with no subscription required. That includes a live preview while you build it and an instant, print-ready PDF download. Subscribers can generate it as part of their plan.`,
  },
  {
    q: "Is this legal advice?",
    a: "No. MintSlip provides a document preparation service, not legal representation, and using our generator does not create an attorney–client relationship. If your situation involves significant money, ongoing threats to your safety, or complex legal questions, you should speak with a licensed attorney in your jurisdiction.",
  },
];

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

export default function CeaseAndDesistLanding() {
  const navigate = useNavigate();
  // the letter's price as the admin set it (Preferences > Document prices)
  const P = priceLabel(useDocPrice("cease-and-desist", 9.99));
  const [openFaq, setOpenFaq] = useState(null);
  const [heroRef, heroInView] = useInView();
  const [whatRef, whatInView] = useInView();
  const [casesRef, casesInView] = useInView();
  const [stepsRef, stepsInView] = useInView();
  const [featuresRef, featuresInView] = useInView();
  const [pricingRef, pricingInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  const go = () => navigate("/app/legal-forms");

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>Cease and Desist Letter Generator | Create One Online for ${PRICE} | MintSlip</title>
        <meta name="description" content={`Create a professional cease and desist letter online in minutes. Stop harassment, defamation, copyright infringement, or debt collector calls. Instant PDF download for $${PRICE}.`} />
        <meta name="keywords" content="cease and desist letter, cease and desist template, cease and desist generator, stop harassment letter, defamation letter, copyright infringement letter, debt collector cease and desist, FDCPA letter, demand letter" />
        <meta property="og:title" content={`Cease and Desist Letter Generator | Create One Online for $${PRICE}`} />
        <meta property="og:description" content="Create a professional cease and desist letter in minutes. Harassment, defamation, copyright, debt collection and more. Instant PDF." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Cease and Desist Letter Generator | MintSlip`} />
        <meta name="twitter:description" content="Stop harassment, defamation, or infringement with a formal demand letter. Instant PDF download." />
        <link rel="canonical" href="https://mintslip.com/cease-and-desist-letter" />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "MintSlip Cease and Desist Letter Generator",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web",
          "offers": { "@type": "Offer", "price": PRICE, "priceCurrency": "USD" },
          "description": "Create a professional cease and desist letter online for harassment, defamation, copyright infringement, debt collection, and breach of contract.",
        })}</script>

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": FAQS.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a },
          })),
        })}</script>
      </Helmet>

      <Header />

      {/* ── HERO ── */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 text-sm font-medium text-slate-700">
              <ShieldAlert className="w-4 h-4 text-emerald-600" />
              Formal legal demand letter
            </div>
          </div>

          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Cease and desist <span className="font-black">letter generator</span>
          </h1>

          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Make it stop, in writing. Create a professional, attorney-style cease and desist letter in minutes
            to demand an end to harassment, defamation, copyright infringement, or unwanted debt collection calls.
          </p>

          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your letter</span>
            </button>
          </div>
          <p className="text-center text-slate-500">
            One-time <span className="font-bold text-slate-900">${PRICE}</span> · Instant PDF download
          </p>

          <div className="flex flex-wrap justify-center gap-x-8 gap-y-3 mt-10 text-sm text-slate-500">
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> No lawyer required</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> Ready in under 5 minutes</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-600" /> Print-ready PDF</span>
          </div>
        </div>
      </section>

      {/* ── WHAT IT IS ── */}
      <section ref={whatRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <div className={`transition-all duration-700 ${whatInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-8">
            What is a <span className="font-black">cease and desist letter</span>?
          </h2>
          <div className="max-w-3xl mx-auto text-slate-600 text-lg leading-relaxed space-y-4">
            <p>
              A <strong>cease and desist letter</strong> is a formal written demand that someone immediately stop a specific
              activity that is harming you or violating your legal rights. It identifies the conduct, states why it is unlawful,
              sets a deadline for compliance, and warns that you are prepared to pursue legal remedies if it continues.
            </p>
            <p>
              It is not a court order, but it is often the most important first step. A well-written letter puts the recipient
              on formal notice, establishes a dated record of your objection, and frequently resolves the problem without the
              time and expense of a lawsuit. If the matter does end up in court, that documented notice works in your favor.
            </p>
            <p>
              MintSlip's generator builds the letter for you using the structure attorneys use: proper letterhead, a subject
              line, factual demand paragraphs tailored to your situation, a compliance deadline, consequences of non-compliance,
              and reservation-of-rights language.
            </p>
          </div>
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section ref={casesRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${casesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          When to send a <span className="font-black">cease and desist</span> letter
        </h2>
        <p className={`text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${casesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Choose your situation and the letter language is tailored automatically, including the relevant statutes.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {USE_CASES.map(({ icon: Icon, title, desc, tone }, i) => {
            const t = toneCard[tone];
            return (
              <div
                key={title}
                className={`rounded-[28px] ${t.bg} p-8 transition-all duration-700 ${casesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <Icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-xl md:text-2xl font-medium mb-3 ${t.big}`}>{title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section ref={stepsRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          How it <span className="font-black">works</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {STEPS(P).map((s, i) => (
            <div
              key={s.n}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-7 text-center transition-all duration-700 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <div className="w-14 h-14 rounded-full bg-emerald-500 text-white text-xl font-bold flex items-center justify-center mx-auto mb-4">
                {s.n}
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-2">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className={`flex justify-center mt-12 transition-all duration-700 delay-300 ${stepsInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <button className="navbar-cta" onClick={go}>
            <FileText size={18} />
            <span>Start your letter</span>
          </button>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section ref={featuresRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          What's <span className="font-black">included</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-7 transition-all duration-700 ${featuresInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <span className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mb-4">
                <Icon className="w-7 h-7 text-white" />
              </span>
              <h3 className="font-display font-bold text-lg text-slate-900 mb-1">{title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section ref={pricingRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Simple <span className="font-black">pricing</span>
        </h2>
        <div className={`max-w-md mx-auto rounded-[28px] bg-[#0b0b0b] p-8 md:p-10 text-center transition-all duration-700 delay-150 ${pricingInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-6">
            <span className="font-display text-5xl font-black text-white">${PRICE}</span>
            <span className="text-slate-300 ml-2">one-time</span>
          </div>
          <ul className="text-left space-y-3 mb-8">
            {[
              "Professionally formatted PDF letter",
              "Tailored language for your situation",
              "Live preview while you build it",
              "Signature and letterhead included",
              "Instant download, no waiting",
            ].map(t => (
              <li key={t} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-300">{t}</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your letter</span>
            </button>
          </div>
          <p className="text-xs text-slate-400 mt-4">No subscription required. Secured by Stripe.</p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section ref={faqRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-12 transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Frequently asked <span className="font-black">questions</span>
        </h2>
        <div className="max-w-3xl mx-auto space-y-3">
          {FAQS.map((f, i) => (
            <div
              key={f.q}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden transition-all duration-700 ${faqInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${100 + i * 60}ms` }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-slate-50 transition-colors"
              >
                <span className="font-semibold text-slate-900">{f.q}</span>
                {openFaq === i
                  ? <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  : <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 text-slate-600 leading-relaxed text-sm">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section ref={ctaRef} className="pb-16 md:pb-24 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Put it <span className="font-black">in writing</span> today
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Take the first formal step toward making it stop. Your letter is ready to download in minutes.
          </p>
          <div className="flex justify-center">
            <button className="navbar-cta" onClick={go}>
              <FileText size={18} />
              <span>Create your cease and desist letter</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── DISCLAIMER ── */}
      <section className="pb-10 px-6 bg-white">
        <div className="max-w-3xl mx-auto p-5 bg-amber-50 border border-amber-200 rounded-2xl">
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>Disclaimer:</strong> MintSlip provides a self-service document preparation tool and is not a law firm.
            Nothing on this page is legal advice, and using this generator does not create an attorney–client relationship.
            Laws vary by state and situation. If your matter involves significant sums, threats to your safety, or complex
            legal questions, consult a licensed attorney in your jurisdiction.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
